import React from 'react';
import { Link } from 'react-router-dom';
import '../HomePage.css';
import './FaqPage.css';
import civitasLogo from '../assets/images/Civitas Signals-small.png';
import consiliumLogo from '../assets/images/Consilium-small.png';
import googlePlayImage from '../assets/images/google-play.png';
import appleStoreImage from '../assets/images/apple-store.png';

const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL ?? 'https://play.google.com/store/apps/details?id=com.safescope.app&utm_source=na_Med';
const APP_STORE_URL = 'https://apps.apple.com/us/app/consilium-safety/id6760517718';


type FaqItem = {
  id: string;
  question: string;
  answer: React.ReactNode;
};

const faqItems: FaqItem[] = [
  {
    id: 'what-is-consilium',
    question: 'What is Consilium?',
    answer: (
      <p>
        Your early warning system for an unpredictable world. Consilium delivers hyper-local, near real-time threat 
        intelligence directly to your phone. 
        While others wait for breaking news, you act on breaking threats. Cut through noise. Stay ahead of danger.      
      </p>
    ),
  },

  {
    id: 'how-work',
    question: 'How Does Consilium Work?',
    answer: (
      <p>
        Advanced AI monitors thousands of data streams 24/7. Police scanners, social media, 
        government systems, weather networks—we track it all. When threats emerge in your area, 
        you know first. Intelligence becomes action in seconds, not hours.
      </p>
    ),
  },
  
  {
    id: 'area-coverage',
    question: 'Is Consilium available in my area?',
    answer: (
      <p>
        Coverage spans 3600+ US cities and expanding daily. 
        Select your city during registration. No coverage yet? Just let us know at support@civitassignals.com. 
        When we arrive, you'll be first to know.
      </p>
    ),
  },
  {
    id: 'platform-availability',
    question: 'Which platforms are supported?',
    answer: (
      <p>
        Consilium is available on iOS and Android. Download links are provided on
        our <Link to="/">homepage</Link> or directly from the Apple App Store and
        Google Play.
      </p>
    ),
  },
  
  {
    id: 'threat-types',
    question: 'What kind of threats does Consilium monitor?',
    answer: (
      <><p>Every threat that impacts your safety:</p><ul>
        <li>Active violence - shootings, attacks, armed incidents</li>
        <li>Civil unrest - protests, riots, street closures</li>
        <li>Severe weather - tornadoes, flash floods, dangerous storms</li>
        <li>Infrastructure failures - power outages, water emergencies, gas leaks</li>
        <li>Traffic emergencies - major accidents, road closures, hazmat spills</li>
        <li>Criminal activity - robbery sprees, assault patterns, theft clusters</li>
        <li>Public safety alerts - missing persons, police operations, evacuations</li>
      </ul><p>We monitor what matters. You decide how to respond.</p></>

    ),
  },
  {
    id: 'consilium-accuracy',
    question: 'How accurate are Consilium alerts?',
    answer: (
      <p>
        Precision is our priority. False alarms erode trust—and trust saves lives. 
        Our AI filters noise, validates sources, and cross-references data before alerting. 
        Accuracy rate exceeds 95%. When Consilium alerts, take action.
      </p>
    ),
  },
  {
    id: 'consilium-community-watch',
    question: 'Can I report threats through Consilium?',
    answer: (
      <p>
       Yes. Your voice strengthens the entire network. Community Watch transforms resident observations into verified intelligence that protects everyone. See something suspicious? Report it directly through the app.
       <br /><br />
       <b>Your report enters our trust-weighted verification system.</b><br /> Other Community Watch members vote on credibility. High-quality reports from trusted contributors carry more weight. False reports get filtered out. Real threats rise to the surface.

       <b>  Verified reports become community-wide alerts.</b><br /> When your submission receives enough upvotes or remains viable after review, it transforms into an active alert for everyone in your area. Your vigilance becomes collective protection.
       <br /><br />
       <b>This is crowdsourced safety that works.</b><br /> No single person sees everything. But together, we see threats before they escalate. Your report could prevent someone else's tragedy. Your observation could save a neighbor's life.
       <br /><br />
       <b>For immediate emergencies, call 911 first.</b><br /> Community Watch handles emerging patterns, suspicious activity, and ongoing concerns that need community awareness.
       <br /><br />
       <b>Report what matters. Trust the network. Protect your community.</b><br />
       <br /><br />
       <b>Your eyes are our early warning system.</b><br />
      </p>
    ),
  },
  {
    id: 'premium-overview',
    question: 'What is Consilium Premium?',
    answer: (
      <p><b>Your proactive shield against emerging threats.</b><br /> Premium doesn't wait for danger to strike—it anticipates, predicts, and positions you ahead of harm. While others react to yesterday's news, you're already three steps ahead of tomorrow's threats.<br /><br />

      <b>This is proactive protection redefined.</b><br /> Premium transforms raw intelligence into predictive power. Monitor four cities simultaneously. Receive Consilium predictive forecasts that spot trouble before it escalates. Access deep analytics that reveal threat patterns others miss entirely.<br /><br />
      
      <b>Stay ahead. Stay alive.</b><br /> Premium subscribers don't just receive alerts—they receive advantage. Roaming coverage follows your every move. Ad-free intelligence keeps you focused when focus saves lives. Interactive crime mapping shows you exactly where threats lurk before you encounter them.<br /><br />
      
      <b>Prevention over reaction. Always.</b><br /> Premium builds your safety perimeter before threats breach it. This isn't monitoring—it's strategic defense. Your family deserves more than notifications. They deserve foresight.<br /><br />
      
      <b>When being proactive means being protected, Premium delivers both.</b></p>
    ),
  },
  {
    id: 'premium-cities',
    question: 'How many cities can I monitor with Consilium Premium?',
    answer: (
      <p><b>Four cities simultaneously.</b><br /> Perfect for multi-location enterprises, 
      security teams overseeing client sites, or frequent travelers. 
      Switch between regions instantly. View consolidated dashboards surfacing critical alerts across 
      every monitored location.<br /><br />
      <b>One command center. Complete coverage.</b>
      </p>
    ),
  },
  {
    id: 'premium-roaming',
    question: 'What is roaming coverage?',
    answer: (
      <p><b>Automatic protection that follows you.</b><br /> Premium adjusts alerts to your real-time GPS position. 
        Commuting? Traveling? Coverage moves with you. No manual switching. No coverage gaps. Peace of mind in motion.<br /><br />
        <b>No manual switching. No coverage gaps. Peace of mind in motion.</b><br /><br />
      </p>
    ),
  },
  {
    id: 'premium-ad-free',
    question: 'How does the ad-free experience work?',
    answer: (
      <p><b>Zero interruptions. Pure intelligence.</b><br /> Premium removes all display and interstitial ads 
      across mobile and web. Critical intelligence never competes with sponsored 
        content—especially during crisis. Stay focused on what matters: your safety.<br /><br />
        <b>No distractions. No interruptions.</b><br /><br />
      </p>
    ),
  },
  {
    id: 'premium-base-score',
    question: 'What is Deep Base Score Analysis?',
    answer: (
      <p><b>Beyond surface-level metrics.</b><br /> Premium unlocks narrative explanations, component breakdowns, 
      and mitigation steps. Understand why scores moved. Know how to respond. Accelerate risk assessments with 
      transparent, actionable data.
      </p>
    ),
  },  
  {
    id: 'premium-predictive-forecasting',
    question: 'What is Consilium Premium predictive forecasting?',
    answer: (
      <p><b>Intelligence that sees around corners.</b><br /> Consilium Premium doesn't just report threats—it helps forecast them. Our AI analyzes millions of data points to identify crime pattern shifts and health outbreak signals before they hit your community.<br /><br />

      <b>Consilium crime forecasting identifies elevated-risk areas before you enter them.</b><br /> Historical incident data, mobility patterns, and environmental triggers combine to pinpoint where violent crime, property theft, and organized criminal activity may emerge. Receive forecasts showing specific offense types, recurring hotspots, and elevated risk windows. Plan routes. Time activities. Stay steps ahead of criminal patterns.<br /><br />
      
      <b>Consilium health forecasting spots outbreak signals while they're still containable.</b><br /> Syndromic surveillance, symptom clusters, and disease transmission models generate early warnings about emerging health threats. Know about food contamination before widespread illness. Detect disease outbreaks before they overwhelm healthcare systems. Protect your family with precious lead time.<br /><br />
      
      <b>This is predictive protection in action.</b><br /> Consilium transforms historical patterns into future intelligence. Confidence intervals show forecast reliability. Driver narratives explain exactly why risks are elevated. No guesswork. No surprises. Just actionable foresight when prevention matters most.<br /><br />
      
      <b>Forecast the threat. Prevent the harm. Consilium Premium makes both possible.</b><br /><br />
      </p>
    ),
  },  
  {
    id: 'premium-watch',
    question: 'Can I collaborate with my neighbors using Consilium Premium?',
    answer: (
      <p><b>Neighborhood Watch features enable secure collaboration.</b><br /> 
      Share verified intelligence with trusted contacts. Coordinate responses across security teams. 
      Build stronger safety networks. Collective vigilance multiplies protection.<br /><br />
        <b>Secure collaboration. Collective vigilance.</b><br /><br />
      </p>
    ),
  },   
  {
    id: 'premium-crime-map',
    question: 'How does the interactive crime mapping work?',
    answer: (
      <p><b>Visualize threats with precision.</b><br /> Filter incidents by severity, category, and recency. 
      Spot patterns others miss. Identify high-risk zones before entering them. Turn data into decisions.
       Transform uncertainty into action.<br /><br />
        <b>No manual switching. No coverage gaps. Peace of mind in motion.</b><br /><br />
      </p>
    ),
  },  
  {
    id: 'premium-value',
    question: 'What makes Consilium Premium worth the investment?',
    answer: (
      <p>Premium delivers outcomes, not just features:
        <ul>
          <li>Broader visibility across four cities plus roaming</li>
          <li>Deeper analytics with base scores and trend analysis</li>
          <li>Predictive intelligence through Consilium Premium forecasts</li>
          <li>Team collaboration via Neighborhood Watch</li>
          <li>Uninterrupted, ad-free monitoring</li>
        </ul>
        <b>From reactive to proactive. From informed to protected.</b>
      </p>
    ),
  },  
  {
    id: 'premium-cost',
    question: 'What is the cost of Consilium Premium?',
    answer: (
      <p><b>$4.99/month.</b><br /> Advanced threat intelligence for less than a coffee shop visit. 
      Your family's safety deserves this level of protection. Premium makes it affordable.<br /><br />
        <b>Your safety, your way.</b><br /><br /> 
      </p>
    ),
  },  
  {
    id: 'premium-trial',
    question: 'Can I try Consilium Premium for free?',
    answer: (
      <p><b>14-day free trial. Zero commitment.</b><br /> Experience predictive intelligence. 
      Feel the difference proactive protection makes. Not convinced? Cancel instantly. 
      Your safety journey starts here.
      <br /><br />Use code <b>TRYPREMIUM</b> at checkout to get 14 days free.
      </p>
    ),
  },  
  {
    id: 'privacy-policy',
    question: 'Where can I review the full privacy policy?',
    answer: (
      <p>
        The complete policy outlines what we collect, how we use it, who we share it with, and the safeguards we apply. Read it any time at{' '}
        <a href="/privacy.html">civitassignals.com/privacy</a>.
      </p>
    ),
  },
  {
    id: 'data-deletion',
    question: 'How do I request my data or account be deleted?',
    answer: (
      <p>
        Follow the guided steps at{' '}
        <a href="/account-deletion.html">civitassignals.com/account-deletion</a>. Submit the email once and our privacy team will confirm and complete verified deletions inside 30 days.
      </p>
    ),
  },
  {
    id: 'support',
    question: 'Who can I contact for additional help?',
    answer: (
      <p>
        For subscription, billing, or technical support, email{' '}
        <a href="mailto:support@civitassignals.com">support@civitassignals.com</a>. We
        typically respond within one business day.
      </p>
    ),
  },
];

const FaqPage: React.FC = () => (
  <div className="faq-page-wrapper">
    <nav className="navbar faq-navbar">
      <div className="logo">
        <Link to="/" aria-label="Consilium home">
          <img src={consiliumLogo} alt="Consilium" className="logo-image logo-image-consilium" />
        </Link>
      </div>
      <ul className="nav-links faq-nav-links">
        <li><Link to="/">Home</Link></li>
        <li><a href="/privacy.html">Privacy Policy</a></li>
        <li><a href="/account-deletion.html">Account Deletion</a></li>
        <li><a href="#questions">FAQ</a></li>
        <li><a href="#download" className="nav-cta">Download</a></li>
      </ul>
    </nav>

    <main className="faq-page" id="top">
      <header className="faq-hero">
        <div className="faq-hero-eyebrow">Consilium Intelligence Platform</div>
        <h1>Frequently Asked Questions</h1>
        <p>
          Everything you need to know about downloading Consilium, upgrading to Premium, and staying protected with
          real-time threat intelligence.
        </p>
        <div className="store-buttons faq-store-buttons" id="download">
          <div className="cta-caption">Download Consilium on Android and iOS →</div>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="store-button google"
            aria-label="Get Consilium on Google Play"
          >
            <img src={googlePlayImage} alt="Google Play Store" className="store-icon" />
          </a>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="store-button apple"
            aria-label="Download Consilium on the App Store"
          >
            <img src={appleStoreImage} alt="Download on the App Store" className="store-icon" />
          </a>
        </div>
        <div className="android-only-badge faq-open-test-banner">
          Consilium is live on Android and iOS. Download the app free and start receiving verified safety intelligence.
        </div>
      </header>

      <section className="faq-fast-links" aria-label="Privacy and account management">
        <div className="faq-fast-card">
          <h2>Privacy Policy</h2>
          <p>See exactly how Consilium collects, uses, and safeguards your information.</p>
          <a href="/privacy.html" className="faq-fast-link">
            View Privacy Policy
          </a>
        </div>
        <div className="faq-fast-card">
          <h2>Account Deletion</h2>
          <p>Request account or data deletion in minutes and track the verification timeline.</p>
          <a href="/account-deletion.html" className="faq-fast-link">
            Request Deletion
          </a>
        </div>
      </section>

      <section className="faq-list" aria-label="Frequently asked questions" id="questions">
        {faqItems.map(({ id, question, answer }) => (
          <article key={id} id={id} className="faq-item">
            <h2>{question}</h2>
            <div className="faq-answer">{answer}</div>
          </article>
        ))}
      </section>

      <section className="faq-cta">
        <h2>Need Something Else?</h2>
        <p>
          Still have questions about Consilium? Reach our team at{' '}
          <a href="mailto:support@civitassignals.com">support@civitassignals.com</a> for additional assistance.
        </p>
        <Link to="/" className="faq-home-link">
          Back to the Consilium homepage
        </Link>
      </section>
    </main>

    <footer className="footer faq-footer">
      <div className="footer-content">
        <div className="footer-section">
          <img src={civitasLogo} alt="Civitas Signals" className="footer-logo" />
          <p>Protecting families with real-time local intelligence.</p>
          <div className="footer-links faq-footer-links">
            <Link to="/">Home</Link>
            <a href="/privacy.html">Privacy Policy</a>
            <a href="/account-deletion.html">Account Deletion</a>
            <Link to="/faq">FAQ</Link>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Civitas Signals, Inc. All rights reserved.</p>
        <div className="store-buttons">
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="store-button google"
            aria-label="Get Consilium on Google Play"
          >
            <img src={googlePlayImage} alt="Google Play Store" className="store-icon" />
          </a>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="store-button apple"
            aria-label="Download Consilium on the App Store"
          >
            <img src={appleStoreImage} alt="Download on the App Store" className="store-icon" />
          </a>
        </div>
      </div>
    </footer>
  </div>
);

export default FaqPage;

